/* Generic Carousel JavaScript for Product Carousels */
/* Used by: most-popular-items.liquid, collection-popular-products.liquid */

class ProductCarousel {
  constructor(sectionId, options = {}) {
    this.sectionId = sectionId;
    this.options = {
      autoplay: false,
      autoplaySpeed: 5000,
      showArrows: true,
      showDots: false,
      productsPerRow: 4,
      swipeAmount: 1,
      ...options
    };
    
    this.init();
  }

  loadSwiper(callback) {
    if (window.Swiper) {
      callback();
    } else {
      const script = document.createElement('script');
      script.src = "https://cdn.jsdelivr.net/npm/swiper@11/swiper-bundle.min.js";
      script.onload = callback;
      document.head.appendChild(script);
    }
  }

  initCarousel() {
    const swiperElement = document.querySelector(`.carousel-swiper-${this.sectionId}`);
    if (!swiperElement) {
      //console.log('Swiper element not found for section:', this.sectionId);
      return;
    }
    
    const isSmallViewport = window.matchMedia('(max-width: 767px)').matches;

    const swiper = new Swiper(swiperElement, {
      speed: 300, // Reduced from 600ms for better performance
      slidesPerView: 1,
      slidesPerGroup: 1,
      spaceBetween: 20,
      loop: false,
      watchSlidesProgress: false, // Disabled to reduce calculations
      effect: 'slide',
      allowTouchMove: true,
      touchRatio: 1,
      touchAngle: 45,
      grabCursor: true,
      // Reduce layout work on mobile by avoiding continuous resize updates
      updateOnWindowResize: isSmallViewport ? false : true,
      resizeObserver: isSmallViewport ? false : true,
      observer: false,
      observeParents: false,
      roundLengths: true,
      // Optimize autoplay settings
      autoplay: this.options.autoplay ? {
        delay: this.options.autoplaySpeed,
        disableOnInteraction: false,
        pauseOnMouseEnter: true, // Better UX
      } : false,
      navigation: this.options.showArrows ? {
        nextEl: `.carousel-btn-next-${this.sectionId}`,
        prevEl: `.carousel-btn-prev-${this.sectionId}`,
        hideOnClick: false,
      } : false,
      pagination: this.options.showDots ? {
        el: `.carousel-pagination-${this.sectionId}`,
        clickable: true,
      } : false,
      // Optimize breakpoints for better performance
      breakpoints: {
        550: {
          slidesPerView: 2,
          slidesPerGroup: 1,
          spaceBetween: 20,
        },
        750: {
          slidesPerView: this.options.productsPerRow,
          slidesPerGroup: this.options.swipeAmount,
          spaceBetween: 20,
        },
        990: {
          slidesPerView: this.options.productsPerRow,
          slidesPerGroup: this.options.swipeAmount,
          spaceBetween: 30,
        }
      },
      // Optimize event handlers to reduce forced reflows
      on: {
        init: (swiperInstance) => {
          // Delay button state update to avoid initial reflow
          requestAnimationFrame(() => this.updateButtonStates(swiperInstance));
        },
        slideChange: (swiperInstance) => {
          // Only update button states, not during transitions
          if (!swiperInstance.animating) {
            this.updateButtonStates(swiperInstance);
          }
        },
        transitionStart: () => {
          // Remove transition class management to avoid conflicts
        },
        transitionEnd: (swiperInstance) => {
          // Update button states after transition completes
          this.updateButtonStates(swiperInstance);
        }
      }
    });
    
    //console.log('Carousel initialized for section:', this.sectionId, swiper);
    return swiper;
  }

  updateButtonStates(swiper) {
    // Use CSS classes instead of direct style manipulation to avoid forced reflows
    const prevBtn = document.querySelector(`.carousel-btn-prev-${this.sectionId}`);
    const nextBtn = document.querySelector(`.carousel-btn-next-${this.sectionId}`);
    
    if (prevBtn) {
      // Remove all state classes first
      prevBtn.classList.remove('disabled', 'enabled');
      // Add appropriate state class
      prevBtn.classList.add(swiper.isBeginning ? 'disabled' : 'enabled');
    }
    if (nextBtn) {
      // Remove all state classes first
      nextBtn.classList.remove('disabled', 'enabled');
      // Add appropriate state class
      nextBtn.classList.add(swiper.isEnd ? 'disabled' : 'enabled');
    }
  }

  addTransitionClass() {
    // Use requestAnimationFrame to avoid forced reflows
    requestAnimationFrame(() => {
      const wrapper = document.querySelector(`.carousel-swiper-${this.sectionId} .swiper-wrapper`);
      if (wrapper) {
        wrapper.style.transition = 'transform 0.6s cubic-bezier(0.25, 0.46, 0.45, 0.94)';
      }
    });
  }

  removeTransitionClass() {
    // Use requestAnimationFrame to avoid forced reflows
    requestAnimationFrame(() => {
      const wrapper = document.querySelector(`.carousel-swiper-${this.sectionId} .swiper-wrapper`);
      if (wrapper) {
        wrapper.style.transition = '';
      }
    });
  }

  init() {
    this.loadSwiper(() => this.initCarousel());
  }
}

// Global function for backward compatibility
function initProductCarousel(sectionId, options = {}) {
  return new ProductCarousel(sectionId, options);
}

// Auto-initialize carousels on page load
document.addEventListener('DOMContentLoaded', function() {
  // This will be called by individual sections
});

// Shopify theme editor support
if (typeof Shopify !== 'undefined' && Shopify.designMode) {
  document.addEventListener('shopify:section:load', (event) => {
    // Re-initialize carousels when sections are loaded in theme editor
    const carouselElements = event.target.querySelectorAll('[class*="carousel-swiper-"]');
    carouselElements.forEach(element => {
      const sectionId = element.className.match(/carousel-swiper-([a-zA-Z0-9]+)/)?.[1];
      if (sectionId) {
        // Re-initialize the carousel for this section
        setTimeout(() => {
          new ProductCarousel(sectionId);
        }, 100);
      }
    });
  });
}
